/**
 * Single source of truth for the user-facing-locale → i18n-file-basename
 * mapping in this monorepo.
 *
 * - **Keys** are BCP-47 user-facing locales as referenced by each app's
 *   `languages.ts` (e.g. `nb-NO`). These are the values shown by
 *   `LanguageProperty`, persisted in the `"language"` user setting, and
 *   consumed by Luxon, MUI `MuiLocalizationProvider`, and `LocaleProvider`.
 * - **Values** are the on-disk i18n file basenames as declared in each app's
 *   `project.json` → `i18n.supportedFiles` (e.g. `no`). These are what
 *   i18next loads from `assets/i18n/<library>/<lng>.json`.
 *
 * Adding a new locale to the monorepo requires three coordinated edits:
 *   1. Add a row here.
 *   2. Add the user-facing code to the app's `languages.ts`.
 *   3. Add the on-disk basename to the app's `project.json` →
 *      `i18n.supportedFiles`.
 *
 * See `docs/prd/2026/05/22/prd-i18n-language-mapping.md`.
 */
export const userFacingToI18nLng: Record<string, string> = {
  "nb-NO": "no",
  "nn-NO": "nn-NO",
  "en-US": "en",
};

/**
 * Maps a user-facing BCP-47 locale (e.g. `nb-NO`) to the i18n file basename
 * i18next should load (e.g. `no`).
 *
 * Throws an {@link UnknownUserFacingLocaleError} if the user-facing locale
 * is not a key in {@link userFacingToI18nLng}.
 *
 * Throws a {@link MappedLngNotSupportedError} if the mapped basename is not
 * present in the calling app's `supportedLngs`. Pass an empty array to skip
 * this second validation (e.g. when the caller has not yet wired
 * `supportedLngs`).
 *
 * @param userFacingLanguage - A BCP-47 user-facing locale.
 * @param supportedLngs - The calling app's i18next `supportedLngs` array, or
 *   `[]` to skip the second validation.
 * @returns The i18n file basename that should be passed to i18next.
 */
export function toI18nLng(
  userFacingLanguage: string,
  supportedLngs: ReadonlyArray<string>,
): string {
  const mapped = userFacingToI18nLng[userFacingLanguage];
  if (mapped === undefined) {
    throw new UnknownUserFacingLocaleError(userFacingLanguage);
  }
  if (supportedLngs.length > 0 && !supportedLngs.includes(mapped)) {
    throw new MappedLngNotSupportedError(
      userFacingLanguage,
      mapped,
      supportedLngs,
    );
  }
  return mapped;
}

export function normalizeInitialUserFacingLanguage(
  storedLanguage: string | null | undefined,
  fallbackUserFacingLanguage: string,
): string {
  if (storedLanguage == null) {
    return fallbackUserFacingLanguage;
  }

  if (userFacingToI18nLng[storedLanguage] !== undefined) {
    return storedLanguage;
  }

  const legacyUserFacingLanguage = Object.entries(userFacingToI18nLng).find(
    ([, i18nLng]) => i18nLng === storedLanguage,
  )?.[0];

  return legacyUserFacingLanguage ?? fallbackUserFacingLanguage;
}

/**
 * Thrown by {@link toI18nLng} when called with a user-facing locale that is
 * not a key in {@link userFacingToI18nLng}.
 */
export class UnknownUserFacingLocaleError extends Error {
  override readonly name = "UnknownUserFacingLocaleError";
  readonly userFacingLanguage: string;
  readonly validUserFacingLocales: ReadonlyArray<string>;

  constructor(userFacingLanguage: string) {
    const validUserFacingLocales = Object.keys(userFacingToI18nLng);
    super(
      `toI18nLng: unknown user-facing locale "${userFacingLanguage}". ` +
        `Valid user-facing locales are: [${validUserFacingLocales
          .map((l) => `"${l}"`)
          .join(", ")}]. ` +
        `To add a new locale, add a row to userFacingToI18nLng in ` +
        `shared-ts-utils-i18n, then add the locale to the app's ` +
        `languages.ts and project.json → i18n.supportedFiles.`,
    );
    this.userFacingLanguage = userFacingLanguage;
    this.validUserFacingLocales = validUserFacingLocales;
  }
}

/**
 * Thrown by {@link toI18nLng} when the mapped i18n file basename is not
 * present in the calling app's `supportedLngs`.
 */
export class MappedLngNotSupportedError extends Error {
  override readonly name = "MappedLngNotSupportedError";
  readonly userFacingLanguage: string;
  readonly mappedLng: string;
  readonly supportedLngs: ReadonlyArray<string>;

  constructor(
    userFacingLanguage: string,
    mappedLng: string,
    supportedLngs: ReadonlyArray<string>,
  ) {
    super(
      `toI18nLng: user-facing locale "${userFacingLanguage}" maps to ` +
        `i18n basename "${mappedLng}", which is not in the calling app's ` +
        `supportedLngs [${supportedLngs.map((l) => `"${l}"`).join(", ")}]. ` +
        `Add "${mappedLng}" to the app's project.json → i18n.supportedFiles ` +
        `(and ensure the locale file exists on disk).`,
    );
    this.userFacingLanguage = userFacingLanguage;
    this.mappedLng = mappedLng;
    this.supportedLngs = supportedLngs;
  }
}
